import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import TextareaAutosize from "react-textarea-autosize";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";
import { RoomCollection } from "@lifeat/shared/data-models/Rooms/RoomEntity";
import { isRoomOwnedByUser } from "@lifeat/shared/rooms/RoomUtil";
import { CHAT_MESSAGE_LENGTH_LIMIT } from "@lifeat/shared/rooms/room-chat/RoomChatConstants";
import { buildChatMessage } from "@lifeat/shared/rooms/room-chat/RoomChatUtil";
import MoreActionsMenu, { ActionDataModel } from "@common/MoreActionsMenu/MoreActionsMenu";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import useDbValueListener from "@hooks/useDbValueListener";
import { FontSize } from "@themes/Fonts";
import { ScrollbarStyles } from "@themes/ScrollbarStyles";
import Styles from "@themes/Styles";
import { useHeadlessPomodoroReadonly } from "../../../widget-workspace/components/Widget/TimerWidget/hooks/useHeadlessPomodoroReadonly";
import { RoomParams } from "../../RoomTypes";
import { ChatMessagesContextType, useChatMessagesContext } from "../ChatMessagesContext";
import { deleteAllRoomChatMessages, sendRoomChatMessage } from "../RoomChatService";
import { ChatMessageDisplayState } from "../RoomChatTypes";
import ChatLoadingIndicator from "./ChatLoadingIndicator";
import SendIcon from "/resources/icons/SendIcon.svg";

const ChatFooter = styled.div`
    width: 100%;
    height: fit-content;
`;

const InputBoxContainer = styled.div`
    width: 100%;
    height: fit-content;
    padding: 10px;
`;

const InputBox = styled.div<{ disabled: boolean }>`
    width: 100%;
    height: fit-content;

    border-radius: ${Styles.widgetBorderRadius};
    border: 1px solid ${(props) => props.theme.borderPrimary};

    pointer-events: ${(props: { disabled: boolean }) => (props.disabled ? "none" : "all")};
    opacity: ${(props: { disabled: boolean }) => (props.disabled ? "0.7" : "1")};
`;

const InputBoxFooter = styled.div`
    width: 100%;
    height: fit-content;
    display: flex;
    justify-content: space-between;
    align-items: center;

    padding: 5px;
`;

const FooterItem = styled.div`
    width: fit-content;
    height: fit-content;
    display: flex;
    padding-right: 5px;
    gap: 5px;
    align-items: center;
`;

const StyledSendIcon = styled(SendIcon)<{ disabled: boolean }>`
    cursor: ${(props: { disabled: boolean }) => (props.disabled ? "not-allowed" : "pointer")};
    fill: ${(props) => props.theme.iconColor};
    transform: scale(0.8);
`;

const StyledInput = styled(TextareaAutosize)`
    width: 100%;
    min-height: 50px;
    max-height: 70px;
    padding: 5px;
    overflow-y: scroll;
    border: none;
    border-radius: inherit;
    resize: none;
    background-color: ${(props) => props.theme.backgroundPrimary};
    color: ${(props) => props.theme.textColorPrimary};
    font-size: ${FontSize.normal};
    font-family: inherit;
    white-space: pre-wrap;

    &:focus,
    &:focus-visible {
        outline: none;
        font-size: ${FontSize.normal};
    }

    ${ScrollbarStyles};
`;

const StyledMoreActionsMenu = styled(MoreActionsMenu)`
    position: relative;

    .more-actions-menu-contents {
        position: absolute;
        bottom: 30px;
        width: 130px;
    }

    * {
        padding: unset;
    }
`;

const CharCountContainer = styled.div`
    color: ${(props) => props.theme.textColorSpecial};
    font-size: ${FontSize.small};
    height: fit-content;
`;

const ClearMessagesLoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
`;

const CLEAR_MESSAGES = "Clear messages";
const INPUT_PLACEHOLDER = "Say something";
const CLEARING_MESSAGES_LABEL = "clearing messages";
const COUNT_WARNING_THRESH = 0.75; // Threshold at which count limit appears in footer
const DELETE_ALL_MESSAGES_CONFIRM_PROMPT =
    "This will permanently erase all messages that have been sent in this Room. Please confirm to continue.";
const BLOCKED_CHAT_INPUT_PLACEHOLDER = "Uh-oh. Looks like you have been muted, and can only view this community";

interface RoomChatFooterProps {
    readonly isInteractingBlocked: boolean;
}
export default function RoomChatFooter(props: RoomChatFooterProps) {
    const { user, userData }: UserContextType = useUserContext();
    const { latestMessages, setLatestMessages }: ChatMessagesContextType = useChatMessagesContext();
    const { roomId }: RoomParams = useParams();

    const isHost = isRoomOwnedByUser(roomId, userData);

    const [input, setInput] = useState("");
    const [charLimitExceeded, setCharLimitExceeded] = useState(false);
    const chatInputState = useChatInput(roomId, props.isInteractingBlocked);

    const [isClearMessagesLoading, setIsClearMessagesLoading] = useState(false);

    useEffect(() => {
        if (props.isInteractingBlocked && input.length > 0) {
            setInput("");
        }
    }, [props.isInteractingBlocked]);

    const handleDeleteAllMessages = () => {
        if (confirm(DELETE_ALL_MESSAGES_CONFIRM_PROMPT)) {
            setIsClearMessagesLoading(true);
            deleteAllRoomChatMessages(roomId).then(() => {
                setIsClearMessagesLoading(false);
            });
        }
    };

    const actions: ActionDataModel[] = [{ name: CLEAR_MESSAGES, actionHandler: handleDeleteAllMessages }];

    const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            // Allow user to shift+enter to create newline in textarea
            e.preventDefault();
            e.stopPropagation();

            handleSendMessage();
        }
    };

    const handleSendMessage = () => {
        if (user && !props.isInteractingBlocked && input.length > 0 && !charLimitExceeded) {
            // add message to list as sending
            const currentMessages = latestMessages;
            const uuid = uuidv4();
            const message: ChatMessageDisplayState = {
                ...buildChatMessage(user.uid, input, false, uuid),
                state: "sending",
            };

            setLatestMessages({
                ...currentMessages,
                [uuid]: message,
            });
            setInput("");

            sendRoomChatMessage({ roomId: roomId, messageText: input, messageUuid: uuid, isRoomHost: isHost }, () =>
                setLatestMessages({ ...currentMessages }),
            );
        }
    };

    const handleTextChange = (input: string) => {
        setInput(input);
        setCharLimitExceeded(input.length > CHAT_MESSAGE_LENGTH_LIMIT);
    };

    const clearLoadingIndicator = (
        <ClearMessagesLoadingContainer>
            <ChatLoadingIndicator label={CLEARING_MESSAGES_LABEL} />
        </ClearMessagesLoadingContainer>
    );

    return (
        <ChatFooter>
            {isClearMessagesLoading && clearLoadingIndicator}
            <InputBoxContainer>
                <InputBox disabled={isClearMessagesLoading}>
                    <StyledInput
                        name="room-chat-input"
                        value={chatInputState.disabled ? "" : input}
                        placeholder={chatInputState.placeholder}
                        autoComplete={"off"}
                        rows={1}
                        onChange={(e) => handleTextChange(e.target.value)}
                        onKeyDown={(e) => handleKeyPress(e)}
                        disabled={chatInputState.disabled}
                    />

                    <InputBoxFooter>
                        <FooterItem>
                            {userData?.roomsData?.personalRoomId === roomId && (
                                <StyledMoreActionsMenu actions={actions} />
                            )}
                            {input.length / CHAT_MESSAGE_LENGTH_LIMIT > COUNT_WARNING_THRESH && (
                                <CharCountContainer>
                                    {input.length} / {CHAT_MESSAGE_LENGTH_LIMIT} characters
                                </CharCountContainer>
                            )}
                        </FooterItem>

                        <FooterItem>
                            <StyledSendIcon
                                className={`chat-send-icon`}
                                onClick={() => handleSendMessage()}
                                disabled={props.isInteractingBlocked}
                            />
                        </FooterItem>
                    </InputBoxFooter>
                </InputBox>
            </InputBoxContainer>
        </ChatFooter>
    );
}

type ChatInputState = {
    disabled: boolean;
    placeholder: string;
};

function useChatInput(roomId: string, isInteractingBlocked: boolean): ChatInputState {
    const { lastEvent: groupPomodoroLastEvent, timerDisplayText } = useHeadlessPomodoroReadonly(roomId);
    const freezeChatOnPomodoro = useDbValueListener(
        RoomCollection.at(roomId).at("groupPomodoro").at("freezeChatOnPomodoro").node,
    );
    const groupPomodoroIsRunning = groupPomodoroLastEvent?.isRunning && groupPomodoroLastEvent?.type === "pomodoro";

    let chatInputState: ChatInputState = {
        disabled: false,
        placeholder: INPUT_PLACEHOLDER,
    };
    if (isInteractingBlocked) {
        chatInputState = {
            disabled: true,
            placeholder: BLOCKED_CHAT_INPUT_PLACEHOLDER,
        };
    } else if (groupPomodoroIsRunning && freezeChatOnPomodoro) {
        chatInputState = {
            disabled: true,
            placeholder: `Chat will resume during break time in ${timerDisplayText}`,
        };
    }
    return chatInputState;
}
